import { ui, always, Scaffolding, s, when, select } from '@owenscorning/pcb.alpha';
import _ from 'lodash';

import { makeT } from '../../../../location/locales';
import Modular from '../Modular';
import Page from '../Page';

const { Validate } = Scaffolding;

const reducedBannerDefaults = language => {
  const t = makeT(language);

  return {
    title: t('product.reduced_banner.title'),
    message: {
      ops: [
        {
          insert: t('product.reduced_banner.message'),
        },
        {
          insert: t('product.reduced_banner.explore_other_products'),
          attributes: {
            link: `/${language}`,
          },
        },
        {
          'insert': '\n'
        },
      ],
    },
  };
};

const Article = (type, dataset, additional, taxonomies, overrides, zoneCategory = null) => Modular(
  type, 'Article',
  _.merge({},
    {
      // custom things for Articles here
      read: ({contents, metadata}) => {
        if(!_.isEmpty(contents)){
          return {contents, metadata};
        }

        let finalMetadata = _.cloneDeep(metadata);

        const availabilityBlock = {
          availability: {
            status: 'active',
            visibility: 'listed',
            reducedBanner: reducedBannerDefaults('en')
          }
        };

        finalMetadata.settings = _.merge(finalMetadata.settings, availabilityBlock);

        return {
          contents: _.merge({}, { layout: { Header: { Hero: {} }, Additional: {
            RelatedArticles: {
              type: {
                source: 'data'
              },
              data: {
                dataset: dataset,
                parameters: { source: "recent", category: "All", recentCount: "3" }
              },
              copy: {
                prehead: '',
                heading: 'Related Articles',
                bodyCopy: ''
              },
              settings: {
                itemsPerRow: 3,
                itemsPerRowTablet: 2,
                aspectRatio: {
                  selection: '2'
                }
              }
            },
            SocialMediaShareBanner: {
              channels: {
                facebook: true,
                twitter: true,
                pinterest: true,
                linkedin: true
              },
              background: {
                color: 'dark'
              },
              content: {
                text: 'Share this page on',
                shareMessage: 'Check out what I found on Owens Corning!'
              }
            }
          } } }, contents),
          finalMetadata
        };
      },
      write: ({contents, metadata}) => {
        return {
          contents,
          metadata
        }
      },
      list: (UI) => ({
        sections: { '0': {
            details: { name: always`Body` },
            modules: ui`List/Item`.of(Modular.Module.List(UI))({
              title: 'Body',
              dropdown: false
            })
          } },
        'layout/Additional': Modular.Layout.List(UI, 'Additional', additional, { dropdown: false })
      }),
      settings: {
        details: ui`List/Item`.of(ui`Form`.of({
          information: ui`Form`.of({
            title: ui`Text`({ label: 'Title', maxLength: '100', flags: { Right: ['100 character limit'] } }),
            description: ui`Text`({ multiline: true, maxLength: '280', flags: { Right: ['280 character limit'] }, validation: Validate.Maximum(280), label: 'Description' }),
            published: ui`Date`({ label: 'Date Published', format: 'ISODateOnly' }),
            writer: ui`Text`({ label: 'Written By' })
          }),
          image: ui`Image`({
            label: 'Featured Image',
            crop: true,
            sublabel: 'This image will be used as a preview throughout blog list pages.'
          }),
          taxonomies
        }))({
          title: 'Article Information',
          startsOpen: true
        }),
        availability: ui`List/Item`.of(
          ui`Form`.of(
            {
              // discontinued (Boolean) or legacy_link.value (for external) on old PDP
              status: ui`Choices`.of({
                active: 'Active',
                reduced: "Reduced",
                discontinued: 'Discontinued',
                external: 'External'
              })({
                label: 'Status',
                mode: ui`Choices/Mode/Dropdown`,
                default: 'active'
              }),
              [s._]: ui`Tip`.of(
                '<strong>Active</strong> means this product is available normally, and will show in appropriate filters/lists.<br>\n' +
                '<strong>Reduced</strong> means the product may have limited availability or may be temporarily unavailable.<br>\n' +
                '<strong>Discontinued</strong> means the PDP will be hidden filters/lists, and the PDP will have a banner on the page indicating to customers that the product has been discontinued.<br>\n' +
                '<strong>External</strong> means the user will be taken to an external URL instead of a product detail page (intended to be used during transition to new site)'
              ),
              reducedBanner: ui`Form`.of({
                title: ui`Text`({
                  label: "Title",
                }),
                message: ui`Text/Rich`({
                  label: "Message Content",
                }),
                dismissable: ui`Switch`({
                  label: "Dismissable",
                  default: false,
                })
              })({
                label: 'Reduced Availability Banner',
                visible: when`../status`.is.equal.to('reduced')
              }),
              // legacy_link.value on old PDP
              externalUrl: ui`Text`({
                label: 'External URL',
                visible: when`../status`.is.equal.to('external')
              }),
              // unlisted (Boolean) on old PDP
              visibility: ui`Choices`.of({
                listed: 'Listed',
                unlisted: 'Unlisted'
              })({
                label: 'PDP Visibility',
                mode: ui`Choices/Mode/Dropdown`,
                default: 'listed'
              }),
              [s._]: ui`Tip`.of('<strong>Listed</strong> means the PDP will show in appropriate filters/lists.<br>\n' +
                '<strong>Unlisted</strong> means the PDP will be hidden from any filters/lists and instead will need to be manually linked to for discoverability.'),
              [s._]: ui`Build/Language`({
                language: select`~build/locale_name`,
                label: 'Language / Location'
              }),
              ...(zoneCategory ? {
                // covered_zones on old PDP
                zones: ui`Choices`.of(MDMS_URL + '/api/v1/product/zones?as=choices')({
                  query: {
                    country_code: select`~build/region`,
                    category: zoneCategory
                  },
                  mode: ui`Choices/Mode/Vertical`,
                  format: ui`Choices/Format/Array`,
                  multiple: true,
                  label: 'PSA / Zone Availability'
                })
              } : {})
            }
          )
        )({
          title: 'Availability & Visibility',
          unwrapped: true,
          standalone: true
        }),
        ..._.omit(Page.Settings, 'details')
      },
      variables: (UI) => ({
        Gap: {
          [UI.Viewport.Device.Desktop]: 32,
          [UI.Viewport.Device.Tablet]: 24
        }
      }),
      selection: {
        in: (selection) => selection == 'sections/0' ? false
          : selection == 'layout/Header/Hero'
            ? { ...Modular.Selection.in(), sidebar: 'settings' }
            : { ...Modular.Selection.in(selection), sidebar: 'builder' },
        out: (focus) => Board.sidebar == 'settings'
          ? 'layout/Header/Hero'
          : Modular.Selection.out(focus)
      },
      onTab: (tab) => Board.select(Board.definition.selection.in(tab == 'settings' ? 'layout/Header/Hero' : null)),
      layout: { Header: [ 'Hero' ], Additional: additional },
      view: (UI, layout) => Modular.Renderer(UI, layout, { section: { selectable: false } }),
    },
    overrides
  )
)

export default Article;
